<script setup>
    import SpeakersModal from '~/components/blocks/two-six/speakers/modal.vue';
    // vaul-vue's DrawerTitle/DrawerDescription (NOT reka-ui's — that injects the
    // wrong context and throws). A custom #header slot replaces UDrawer's default
    // header where the `:title` prop would render, so we supply the required,
    // sr-only title/description here instead.
    import { DrawerTitle, DrawerDescription } from 'vaul-vue';
    import { VisuallyHidden } from 'reka-ui';
    const { $bus, $clientPosthog } = useNuxtApp();
    
    const props = defineProps({
        speaker_id: {
            type: String,
            required: true
        },
        speaker: {
            type: Object,
            default: () => ({})
        }
    });

    const open = defineModel('open');
    const route = useRoute();
    const router = useRouter();
    
    // Resolve the speaker across known event years — try 2026 (Sessionboard)
    // first, then fall back to 2025 (Airtable) so legacy speaker slugs still
    // work.
    const findMatch = (speakers, id) => {
        if (!Array.isArray(speakers)) return null;
        const needle = id?.toLowerCase?.();
        return speakers.find((s) => {
            const sid = s?.id?.toLowerCase?.();
            const slug = s?.slug?.toLowerCase?.();
            return sid === needle || slug === needle;
        }) || null;
    };

    const { data, pending, error, refresh } = await useAsyncData(
        `speaker-${props.speaker_id}`,
        async () => {
            const id = props.speaker_id;
            try {
                const res2026 = await $fetch('/api/v2/speakers', { params: { id, year: 2026, limit: 1 } });
                const match2026 = findMatch(res2026?.speakers, id);
                if (match2026) return { speakers: [match2026] };
            } catch {}
            try {
                const res2025 = await $fetch('/api/v2/speakers', { params: { id, year: 2025, limit: 1 } });
                const match2025 = findMatch(res2025?.speakers, id);
                if (match2025) return { speakers: [match2025] };
            } catch {}
            return { speakers: [] };
        },
    );

    const page = computed(() => {
        if (!data.value?.speakers?.length) return false
        return { id: data.value.speakers[0].id, data: data.value.speakers[0], model: 'speakers' }
    })

    const back = () => {
        $clientPosthog?.capture('speaker_drawer_back_clicked', {
            speaker_id: props.speaker_id,
            speaker_name: page.value?.data?.name
        });
        // Guarded the way session.vue's back() is: with no earlier entry to
        // return to (the drawer reached by a direct link, or a fresh tab),
        // history.back() would take the visitor off the site entirely. Closing
        // the drawer is the sane fallback.
        if (typeof window !== 'undefined' && window.history.length > 1) window.history.back()
        else open.value = false
    }

    const drawerStyles = {
        content: 'bg-[var(--pink)] border-0 ring-0 rounded-none p-0 w-screen md:w-auto max-w-[95vw] md:max-w-[95vw] lg:max-w-[1120px]',
        overlay: 'bg-[var(--purple)]/80 backdrop-blur-xs',
        container: 'speaker-modal pb-[2.75rem] gap-0 bg-transparent',
        header: 'pt-[1.5rem] pb-[1rem] px-[1.22875rem] md:px-[2.5rem] speaker-modal-header flex items-center justify-between',
        title: 'sr-only',
        description: 'sr-only',
        body: 'speaker-modal-body flex flex-col gap-0 p-[1.22875rem] md:p-[2.5rem] m-auto w-full min-w-0',
    }

    watch(() => props.speaker_id, (new_value) => {
        if (new_value) refresh();
    });

    const handleClose = () => {
        const currentQuery = { ...route.query };
        // Only strip the drawer query if it still points at a speaker drawer.
        // If it's already been pushed to a different drawer (e.g. a session),
        // leave it alone so that drawer can take over.
        if (typeof currentQuery.drawer === 'string' && /^\/speakers\//.test(currentQuery.drawer)) {
            delete currentQuery.drawer;
            router.replace({ query: currentQuery });
        }
        $bus.emit('drawer-close');
        // Let a reopen of the same speaker record a fresh open.
        capturedFor.value = null;
        $clientPosthog?.capture('speaker_drawer_closed', {
            speaker_id: props.speaker_id,
            speaker_name: page.value?.data?.name
        });
    }

    const handleOpen = (val) => {
        if (val) $bus.emit('drawer-open');
    }

    // Same fix as the session drawer: the speaker is fetched async, so capturing
    // on @update:open sent speaker_name: undefined for ~40% of opens. Wait for
    // the fetch to settle so the name is present.
    const capturedFor = ref(null);

    const captureOpen = () => {
        const id = props.speaker_id;
        if (!open.value || !id || pending.value) return;
        if (capturedFor.value === id) return;
        capturedFor.value = id;

        $clientPosthog?.capture('speaker_drawer_opened', {
            speaker_id: id,
            speaker_name: page.value?.data?.name ?? null,
        });
        // Record a pageview against the canonical speaker URL so the
        // drawer counts the same as a direct visit to /speakers/{slug}.
        const slug = id.toLowerCase();
        if (typeof window !== 'undefined') {
            const url = `${window.location.origin}/speakers/${slug}`;
            $clientPosthog?.capture('$pageview', {
                $current_url: url,
                $pathname: `/speakers/${slug}`,
                speaker_id: slug,
                speaker_name: page.value?.data?.name ?? null,
                via: 'drawer',
            });
        }
    }

    watch([open, pending, () => props.speaker_id], captureOpen, { immediate: true });

</script>

<template>
    <UDrawer
        class="speaker-drawer"
        v-model:open="open"
        direction="right"
        :dismissable="true"
        :handle="false"
        :ui="drawerStyles"
        @update:open="handleOpen"
        @close="handleClose"
        :handleOnly="true">
        <template #header>
            <div class="inner">
                <button type="button"
                    @click="back"
                    aria-label="Go back to the previous page"
                    class="back-link inline-flex items-center gap-[11px] cursor-pointer bg-transparent border-none p-0
                        font-hubspot-sans font-medium text-[12px] leading-[1.5] tracking-[0.84px] uppercase
                        text-[#7d4566] hover:text-[var(--magenta)] transition-colors">
                    <svg width="7" height="7" viewBox="0 0 8.5 8.5" fill="none" aria-hidden="true">
                        <path d="M7.75 4.25H0.75M0.75 4.25L4.05556 0.750002M0.75 4.25L4.05556 7.75"
                            stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Back
                </button>
                <button type="button"
                    @click="open = false"
                    aria-label="Close speaker details"
                    class="close-btn inline-flex items-center justify-center cursor-pointer
                        w-[42px] h-[42px] rounded-full border border-[var(--magenta)] bg-transparent
                        text-[var(--magenta)] hover:bg-[var(--magenta)] hover:text-white
                        transition-colors">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                        <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                    </svg>
                </button>
            </div>
        </template>
        <template #body>
            <VisuallyHidden>
                <DrawerTitle>{{ page?.data?.name || 'Speaker Details' }}</DrawerTitle>
                <DrawerDescription>{{ page?.data?.job_title || 'Speaker information' }}</DrawerDescription>
            </VisuallyHidden>
            <SpeakersModal :speaker="page.data" v-if="speaker_id && page?.data" :key="speaker_id" :class="pending ? 'blur-lg' : '' " />
        </template>
    </UDrawer>
</template>

<style lang="scss">

.speaker-drawer {
    z-index: 1;

    .inner {
        width: 100%;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
    }
}

.speaker-modal-header {
    background-color: var(--pink);
}

.speaker-modal {

    .container {
        width: 100%;
        max-width: 100%;
        margin: 0;
    }

    .back-button {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        background-color: transparent;
        color: var(--magenta);
        font-family: var(--font-hubspot-sans);
        font-size: 0.8125rem;
        font-weight: 500;
        line-height: 1.25rem;
        letter-spacing: 0.040625rem;
        text-transform: uppercase;
        padding: 0;

        svg {
            stroke: var(--magenta);
        }

        @include hover {
            color: var(--magenta);
            text-decoration: underline dotted var(--magenta);
            text-underline-offset: 3px;
            text-decoration-thickness: 1px;

            svg {
                stroke: var(--magenta);
            }
        }
    }    
}

</style>